import { Point } from '../../math/point';
import { normalizeAngleSigned } from '../../math/angle';
import { vector } from '../../math/vector';
import { Entity } from '../../model/entity';
import { World } from '../../model/world'
import { GameUpdate, GameUpdateState } from '../game-update';
import { PointMotion } from './point-motion';
import clone from 'immutability-helper'

abstract class MotionState extends GameUpdateState {
  constructor(readonly id: string, readonly destination: Point) { super() }

  abstract move(world: World, body: Entity, elapsed: number): World

  update(world: World, elapsed: number): World {
    const body = world.getBody(this.id)
    if (body !== undefined) {
      return this.move(world, body, elapsed)
    }
    return this.completed(world)
  }
}

export class EntityMotion extends GameUpdate {
  constructor(readonly id: string, readonly destination: Point) {
    super(new RotationMotionState(id, destination))
  }
}

class RotationMotionState extends MotionState {
  move(world: World, body: Entity, elapsed: number): World {
    const v = vector(body.position, this.destination)
    const course = v.angle()

    const alpha = normalizeAngleSigned(body.heading - course)
    if (Math.abs(alpha) > 0.05) {
      const diff = Math.sign(alpha) * 0.05
      const newHeading = body.heading - diff
      return world.setBody(this.id, clone(body, {heading: {$set: newHeading}}))
    } else {
      const newWorld = world.setBody(this.id, clone(body, {heading: {$set: course}}))
      return this.changeState(newWorld, new ForwardMotionState(this.id, this.destination))
    }
  }
}

class ForwardMotionState extends MotionState {
  protected pointMotion: PointMotion
  constructor(readonly id: string, readonly destination: Point) {
    super(id, destination)
  }

  enterState(world: World): World {
    const source = world.getBody(this.id)
    if (source !== undefined) {
      this.pointMotion = new PointMotion(source.position, this.destination, 0.075)
    }
    return world
  }

  move(world: World, body: Entity, elapsed: number): World {
    const [p, remaining] = this.pointMotion.arrive(body.position, elapsed)

    const newBody = clone(body, {position: {$set: p}})
    const newWorld = world.setBody(this.id, newBody)

    if (remaining === 0) {
      return newWorld
    } else {
      return this.completed(newWorld)
    }
  }
}
